import { useState, useEffect, useCallback } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Command } from 'cmdk'
import { useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Calendar, Users, Settings, Inbox, Megaphone,
  Target, FileText, Camera, MapPin, UserCheck, MessageSquare, CreditCard,
  PlusCircle, Search, ArrowRight,
} from 'lucide-react'

const NAV_ITEMS = [
  { label: 'Overview', icon: LayoutDashboard, tab: 'overview' },
  { label: 'Bookings', icon: Calendar, tab: 'bookings' },
  { label: 'Customers', icon: Users, tab: 'customers' },
  { label: 'Services', icon: Settings, tab: 'services' },
  { label: 'Availability', icon: Calendar, tab: 'availability' },
  { label: 'Inbox (SMS)', icon: Inbox, tab: 'inbox' },
  { label: 'Marketing', icon: Megaphone, tab: 'marketing' },
  { label: 'Leads', icon: Target, tab: 'leads' },
  { label: 'Estimates', icon: FileText, tab: 'estimates' },
  { label: 'Intake Forms', icon: Camera, tab: 'intake' },
  { label: 'Route Planner', icon: MapPin, tab: 'route' },
  { label: 'Staff', icon: UserCheck, tab: 'staff' },
  { label: 'SMS Logs', icon: MessageSquare, tab: 'sms-logs' },
  { label: 'Account', icon: CreditCard, tab: 'account' },
]

interface CommandPaletteProps {
  onTabChange?: (tab: string) => void
}

export function CommandPalette({ onTabChange }: CommandPaletteProps) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(o => !o)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const handleNav = useCallback((tab: string) => {
    onTabChange?.(tab)
    setOpen(false)
  }, [onTabChange])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9998] flex items-start justify-center pt-[15vh] bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            onClick={e => e.stopPropagation()}
            className="glass-card w-full max-w-lg overflow-hidden border border-white/10"
          >
            <Command className="[&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:border-white/10">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
                <Search className="w-4 h-4 text-muted shrink-0" />
                <Command.Input
                  placeholder="Search or jump to..."
                  className="bg-transparent outline-none text-sm text-foreground placeholder:text-muted w-full"
                />
              </div>
              <Command.List className="max-h-80 overflow-y-auto p-2">
                <Command.Empty className="text-sm text-muted text-center py-6">No results found.</Command.Empty>

                <Command.Group heading={<span className="text-xs text-muted px-2 py-1 uppercase tracking-wider">Navigate</span>}>
                  {NAV_ITEMS.map(item => (
                    <Command.Item
                      key={item.tab}
                      value={`navigate ${item.label}`}
                      onSelect={() => handleNav(item.tab)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm cursor-pointer text-foreground data-[selected=true]:bg-accent/10 data-[selected=true]:text-accent transition-colors"
                    >
                      <item.icon className="w-4 h-4 text-muted shrink-0" />
                      <span>{item.label}</span>
                      <ArrowRight className="w-3 h-3 text-muted ml-auto" />
                    </Command.Item>
                  ))}
                </Command.Group>

                <Command.Separator className="h-px bg-white/5 my-2" />

                <Command.Group heading={<span className="text-xs text-muted px-2 py-1 uppercase tracking-wider">Quick Actions</span>}>
                  {[
                    { label: '+ New Booking', icon: PlusCircle, action: () => handleNav('bookings') },
                    { label: '+ New Customer', icon: PlusCircle, action: () => handleNav('customers') },
                    { label: '+ New Estimate', icon: PlusCircle, action: () => handleNav('estimates') },
                  ].map(item => (
                    <Command.Item
                      key={item.label}
                      value={item.label}
                      onSelect={item.action}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm cursor-pointer text-foreground data-[selected=true]:bg-accent/10 data-[selected=true]:text-accent transition-colors"
                    >
                      <item.icon className="w-4 h-4 text-accent/60 shrink-0" />
                      <span>{item.label}</span>
                    </Command.Item>
                  ))}
                </Command.Group>
              </Command.List>

              <div className="px-4 py-2 border-t border-white/5 flex items-center gap-4 text-xs text-muted">
                <span><kbd className="font-mono bg-white/5 px-1.5 py-0.5 rounded">↑↓</kbd> navigate</span>
                <span><kbd className="font-mono bg-white/5 px-1.5 py-0.5 rounded">↵</kbd> select</span>
                <span><kbd className="font-mono bg-white/5 px-1.5 py-0.5 rounded">esc</kbd> close</span>
              </div>
            </Command>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
